import { PreEmptedSpotDetails, MakeGoodSuggestionDetails } from './types';
import { useMakeFoodRecommendation } from '../context/MakeGoodContext';
import { useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Modal from './Modal';

interface EmailTableProps {
  data: MakeGoodSuggestionDetails[] | PreEmptedSpotDetails[];
  showCreateOfferButton?: boolean;
  createOffer?: (
    record: MakeGoodSuggestionDetails | PreEmptedSpotDetails
  ) => void;

  preEmptedSpotDetails?: PreEmptedSpotDetails;
  isPreEmptedSpotDetails?: boolean;
}

const EmailTable: React.FC<EmailTableProps> = ({
  data,
  showCreateOfferButton,
  createOffer,
  preEmptedSpotDetails,
  isPreEmptedSpotDetails,
}) => {
  const { offerCreated, currentPreEmptedSpot } = useMakeFoodRecommendation();
  const [selectedRecord, setSelectedRecord] =
    useState<MakeGoodSuggestionDetails | null>(null);

  // Type guard to check if record is of type MakeGoodSuggestionDetails
  const isMakeGoodSuggestionDetails = (
    record: MakeGoodSuggestionDetails | PreEmptedSpotDetails
  ): record is MakeGoodSuggestionDetails => {
    return (record as MakeGoodSuggestionDetails).week_of !== undefined;
  };

  const handleOpenModal = (record: MakeGoodSuggestionDetails) => {
    setSelectedRecord(record);
  };

  const handleCloseModal = () => {
    setSelectedRecord(null);
  };

  return (
    <div className="border-1 rounded-[10px] border-snow-white border-separate overflow-hidden">
      <table className="w-full text-left">
        <thead className="border-b-1 border-wo-grey">
          <tr className="font-sans font-bold text-[12px]">
            <th className="p-2">Program</th>
            <th className="p-2">
              {isPreEmptedSpotDetails ? 'Air Date' : 'Week Of'}
            </th>
            <th className="p-2">Daypart</th>
            <th className="p-2">Len</th>
            <th className="p-2">Time</th>
            <th className="p-2">Days</th>
            {!isMakeGoodSuggestionDetails(data[0]) && (
              <>
                <th className="p-2">Rate</th>
                <th className="p-2">Rtg</th>
                <th className="p-2">CPP</th>
                <th className="p-2">Imps</th>
                <th className="p-2">CPM</th>
              </>
            )}
            <th className="p-2"></th>
          </tr>
        </thead>
        <tbody className="font-sans text-left font-normal text-[12px] text-navy-black">
          {data?.map(
            (
              record: MakeGoodSuggestionDetails | PreEmptedSpotDetails,
              index: number
            ) => (
              <tr key={index} className="border-b-1 border-light-grey">
                <td className="p-2">{record.programName}</td>
                <td className="p-2">
                  {isMakeGoodSuggestionDetails(record)
                    ? `${record.week_of}`
                    : `${record.intended_air_date?.split(' ')[0]} `}
                </td>
                <td className="p-2">{record.daypart}</td>
                <td className="p-2">{record.length}</td>
                <td className="p-2">{record.time}</td>
                <td className="p-2">{record.days}</td>
                {!isMakeGoodSuggestionDetails(record) && (
                  <>
                    <td className="p-2">
                      $
                      {record.rate
                        ? Number(record.rate).toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })
                        : ''}
                    </td>
                    <td className="p-2">{record.rtg}</td>
                    <td className="p-2">
                      $
                      {parseFloat(record.cpp.toString()).toLocaleString(
                        'en-US',
                        {
                          minimumFractionDigits: 2,
                        }
                      )}
                    </td>
                    <td className="p-2">
                      {record.imp !== undefined
                        ? `${parseFloat(record.imp.toString()).toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                            }
                          )}`
                        : ''}
                    </td>
                    <td className="p-2">
                      {record.cpm !== undefined
                        ? `$${parseFloat(record.cpm.toString()).toLocaleString(
                            'en-US',
                            {
                              minimumFractionDigits: 2,
                            }
                          )}`
                        : ''}
                    </td>
                  </>
                )}

                {showCreateOfferButton ? (
                  <td className="p-2">
                    <button
                      disabled={
                        offerCreated ||
                        currentPreEmptedSpot?.status === 'offer_created' ||
                        preEmptedSpotDetails?.status === 'offer_created'
                      }
                      onClick={() => createOffer?.(record)}
                      className={`text-[12px] font-semibold font-sans ${
                        offerCreated ||
                        currentPreEmptedSpot?.status === 'offer_created' ||
                        preEmptedSpotDetails?.status === 'offer_created'
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-wo-blue cursor-pointer'
                      }`}
                    >
                      + Create Offer
                    </button>
                  </td>
                ) : (
                  <td className="p-2"></td>
                )}

                {isMakeGoodSuggestionDetails(record) ? (
                  <td className="p-1">
                    <span
                      className="text-gray-500 cursor-pointer"
                      onClick={() => handleOpenModal(record)}
                    >
                      <InfoOutlinedIcon />
                    </span>
                  </td>
                ) : (
                  ''
                )}
              </tr>
            )
          )}
        </tbody>
      </table>
      <Modal
        selectedRecord={selectedRecord}
        handleCloseModal={handleCloseModal}
      />
    </div>
  );
};

export default EmailTable;
